import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 5: "Sorting Fundamentals" (Master Spec Section 5), campaign
 * levels 41-50. Teaches: ordering, ascending, descending, comparison, swap, position, sorted vs unsorted.
 * Algorithm(s) used in this world: bubble-sort.
 */
export const world05SortingFundamentalsLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-041",
    world: 5,
    levelInWorld: 1,
    globalLevelNumber: 41,
    algorithm: "bubble-sort",
    values: [4, 3, 9, 7],
    sortDirection: "ascending",
    learningObjectives: [
      "Learn what sorted means: every item in order from smallest to largest.",
      "See a single swap turn an almost-sorted list into a fully sorted one.",
    ],
    notes: "Only the first pair is out of order -- one swap completes the sort.",
  }),
  createCampaignLevel({
    id: "campaign-042",
    world: 5,
    levelInWorld: 2,
    globalLevelNumber: 42,
    algorithm: "bubble-sort",
    values: [8, 2, 6, 5],
    sortDirection: "ascending",
    learningObjectives: [
      "Practice comparing two adjacent items and deciding swap or keep.",
      "See position change as a direct result of a swap decision.",
    ],
    notes: "A straightforward 4-item unsorted list -- practice the compare-then-decide loop.",
  }),
  createCampaignLevel({
    id: "campaign-043",
    world: 5,
    levelInWorld: 3,
    globalLevelNumber: 43,
    algorithm: "bubble-sort",
    values: [11, 4, 14, 2, 9],
    sortDirection: "ascending",
    learningObjectives: [
      "Apply the same compare-and-swap idea to one more item than before.",
      "Reinforce ascending order as smallest-to-largest, left to right.",
    ],
    notes: "A 5-item unsorted list, sorted ascending -- one item more than level 42.",
  }),
  createCampaignLevel({
    id: "campaign-044",
    world: 5,
    levelInWorld: 4,
    globalLevelNumber: 44,
    algorithm: "bubble-sort",
    values: [3, 7, 2, 9],
    sortDirection: "descending",
    learningObjectives: [
      "Learn descending order: largest to smallest, the mirror of ascending.",
      "See that the same compare-and-swap idea applies, just with the comparison reversed.",
    ],
    notes: "First descending-order level -- largest values move to the front instead of the back.",
  }),
  createCampaignLevel({
    id: "campaign-045",
    world: 5,
    levelInWorld: 5,
    globalLevelNumber: 45,
    algorithm: "bubble-sort",
    values: [12, 5, 18, 9, 3],
    sortDirection: "descending",
    learningObjectives: [
      "Practice descending order on a slightly larger dataset.",
      "Contrast which pairs get swapped here versus in an ascending sort of the same values.",
    ],
    notes: "Same size as level 43 but sorted largest-to-smallest instead.",
  }),
  createCampaignLevel({
    id: "campaign-046",
    world: 5,
    levelInWorld: 6,
    globalLevelNumber: 46,
    algorithm: "bubble-sort",
    values: [2, 5, 8, 11],
    sortDirection: "ascending",
    learningObjectives: [
      "See what an already-sorted dataset looks like: no swap is ever needed.",
      "Understand sorted as a property the data can already have, not something that only happens through swapping.",
    ],
    notes: "This dataset is already in ascending order -- every comparison should end in keep, never swap.",
  }),
  createCampaignLevel({
    id: "campaign-047",
    world: 5,
    levelInWorld: 7,
    globalLevelNumber: 47,
    algorithm: "bubble-sort",
    values: [10, 8, 6, 3],
    sortDirection: "ascending",
    learningObjectives: [
      "See a fully reverse-ordered dataset need a swap at every single comparison.",
      "Contrast this maximum-swap case with level 46's zero-swap case.",
    ],
    notes: "This list is in exactly the opposite order from the goal -- every adjacent pair must be swapped.",
  }),
  createCampaignLevel({
    id: "campaign-048",
    world: 5,
    levelInWorld: 8,
    globalLevelNumber: 48,
    algorithm: "bubble-sort",
    values: [9, 4, 7, 1],
    sortDirection: "descending",
    learningObjectives: [
      "Practice descending order once more on an unsorted dataset.",
      "Build comfort switching between ascending and descending goals.",
    ],
    notes: "An unsorted 4-item list, this time sorted from largest to smallest.",
  }),
  createCampaignLevel({
    id: "campaign-049",
    world: 5,
    levelInWorld: 9,
    globalLevelNumber: 49,
    algorithm: "bubble-sort",
    values: [6, 13, 4, 10, 2],
    sortDirection: "ascending",
    learningObjectives: [
      "Apply everything so far -- comparison, swap, keep, position -- on a 5-item ascending sort.",
      "Predict which pairs will swap before making each decision.",
    ],
    notes: "A mixed unsorted 5-item list -- a full practice run before the capstone.",
  }),
  createCampaignLevel({
    id: "campaign-050",
    world: 5,
    levelInWorld: 10,
    globalLevelNumber: 50,
    algorithm: "bubble-sort",
    values: [15, 3, 11, 7, 9],
    sortDirection: "ascending",
    learningObjectives: [
      "Consolidate Sorting Fundamentals: ordering, comparison, swap, position, and sorted vs unsorted.",
      "Complete a full ascending sort from a genuinely shuffled starting order.",
    ],
    notes: "World 5 capstone -- everything this world taught, applied together on one dataset.",
  }),
];
